import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';

extend([a11yPlugin]);

/** WCAG contrast ratio from Colord; invalid or unsupported values fail closed. */
export function contrastRatio(foreground: string, background: string): number {
  const fg = colord(foreground);
  const bg = colord(background);
  if (!fg.isValid() || !bg.isValid()) return Number.NaN;
  return fg.contrast(bg);
}

/** Pick the stronger literal black/white foreground for a colored control. */
export function blackOrWhiteForeground(background: string): '#000000' | '#ffffff' {
  const color = colord(background);
  if (!color.isValid()) return '#000000';
  return color.contrast('#000000') >= color.contrast('#ffffff') ? '#000000' : '#ffffff';
}

/** Preserve a theme-authored text color when readable, otherwise choose black or white. */
export function readableForeground(background: string, preferred?: string | null, minimum = 4.5): string {
  const preferredColor = preferred?.trim();
  if (preferredColor && contrastRatio(preferredColor, background) >= minimum) return preferredColor;
  return blackOrWhiteForeground(background);
}

/**
 * Keep a brand hue while moving it to the nearest shade readable as text on
 * every supplied surface. Use black/white only when the input cannot be parsed.
 */
export function readableColorOnSurfaces(color: string, surfaces: string[], minimum = 4.5): string {
  const parsed = colord(color);
  if (!parsed.isValid()) return blackOrWhiteForeground(surfaces[0] || '#ffffff');
  if (surfaces.every((surface) => contrastRatio(color, surface) >= minimum)) return color;

  const { h, s, l } = parsed.toHsl();
  const candidates = Array.from({ length: 101 }, (_, lightness) => lightness)
    .sort((a, b) => Math.abs(a - l) - Math.abs(b - l));
  for (const lightness of candidates) {
    const candidate = colord({ h, s, l: lightness }).toHex();
    if (surfaces.every((surface) => contrastRatio(candidate, surface) >= minimum)) return candidate;
  }
  return blackOrWhiteForeground(surfaces[0] || '#ffffff');
}

/** Keep the template/theme text role and adjust a branded control fill only if required. */
export function colorReadableWithForeground(color: string, foreground: string, minimum = 4.5): string {
  const parsed = colord(color);
  if (!parsed.isValid()) return color;
  if (contrastRatio(foreground, color) >= minimum) return color;

  const { h, s, l } = parsed.toHsl();
  const candidates = Array.from({ length: 101 }, (_, lightness) => lightness)
    .sort((a, b) => Math.abs(a - l) - Math.abs(b - l));
  for (const lightness of candidates) {
    const candidate = colord({ h, s, l: lightness }).toHex();
    if (contrastRatio(foreground, candidate) >= minimum) return candidate;
  }
  return color;
}
